import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';

interface MagicAfflictionsPageProps {
}

const MagicAfflictionsPage = (props: MagicAfflictionsPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok: Magic Afflictions - Chase the Fox`,
        description: "Learn about the various magical afflictions in Casarok's magic system!",
        keywords: ["casarok", "magic", "magic compendium", "magic system", "magic school", "magical afflictions", "status conditions"],
        imgSrc: `chase/world/magic/afflictions/preview.png`,
        url: `world/magic/afflictions`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="Magical Afflictions">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Magic can be a dangerous study in the world of Casarok. A spellcaster can be inflicted with
                        various status effects that can cause them great harm. On the other hand, they can cause others
                        to have their own afflictions that can be difficult to treat. Below are some common afflictions
                        caused by the forces of magic.
                    </p>
                    <p>
                        <strong>Mana Sickness</strong> is caused by a spellcaster expending mana beyond the zero-mana
                        threshold. Symptoms include coughing, paleness of the skin or fur, and chills. Symptoms become
                        more severe the further beyond zero-mana they go, and extreme cases can lead to permanent
                        maximum mana reduction, muscular atrophy, coma, or even death. Treatment of mana sickness
                        involves not only treating the symptoms but also using special mana potions or food to restore
                        the life force of the afflicted.
                    </p>
                    <p>
                        <strong>Mana Addiction</strong> is caused when a spellcaster gains mana beyond their maximum
                        mana pool on a regular basis. This affliction is noted by the physiological need to imbibe mana
                        potions constantly. Symptoms include euphoria, nervousness, irritability, paleness of the skin
                        or fur, jittery movements, and spontaneity in casting spells. Treatment is similar to that of a
                        standard drug addiction.
                    </p>
                    <p>
                        <strong>Accelerated Aging</strong> is a common affliction among magic users. While not
                        life-threatening, it can make an anthro look older than they actually are. Seasoned mages
                        are particularly noted by their white hair and grey muzzles.
                    </p>
                    <p>
                        <strong>Mana Disconnection</strong> is when a spellcaster cannot access their mana to
                        cast any spells. This affliction is often caused by the use of anti-magic. Immobilization
                        or transformation into an inanimate state can also forcefully divide their soul from their
                        life force to achieve this effect. Treatment involves the removal of whichever effect
                        caused the disconnect in the first place.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/fire">Burn</NavLink> is a
                        status that inflicts a loss of strength and defense in the areas affected. Burns are treated
                        as an ordinary medical condition based on severity. <em>Dire burn</em> causes a complete loss of
                        strength and defense in the target area and can only be inflicted by fire mages.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/frost"><span>Freeze</span></NavLink> is a
                        magical status that immobilizes an area of the body but raises magical defenses. Freezes are
                        treated medically like frostbite and hypothermia. <em>Deep freezes</em> encase the entire 
                        target in ice and completely immobilizes them, but it can only be inflicted by frost mages.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/lightning">Paralysis</NavLink> can
                        cause numbness or loss of sensation in the target and reduce speed or dexterity in the
                        affected body parts. It can also cause immobility in those areas of the body. The status
                        can be treated medically, but magic is often needed to restore full sensation depending on
                        the severity. <em>Full paralysis</em> causes a full loss of dexterity and speed in the target 
                        and complete immobilization. This extreme condition is only caused by lightning magic.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/space">Distortion</NavLink> causes
                        a loss of perception and spatial awareness in the target, especially in affected body parts.
                        Distortion must be treated magically. Reality distortion causes a complete loss of perception
                        in the target and is only capable by using space magic.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/water">Sleep</NavLink> causes
                        the target to slumber, preventing all physical attacks and movement. Anthros can awaken by
                        normal means and can still mumble spells while unconscious. <em>Deep sleep</em> requires medical
                        attention or magical intervention and puts the target into a state of hibernation. Deep
                        sleep can only be caused by the soothing power of water magic.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/wind">Silence</NavLink> creates a
                        condition regarding loss of voice, hoarseness, or even full muteness in the target. While these
                        effects can go away with time, full treatment requires the use of magic. <em>Full silence</em>,
                        which can only be used by wind thaumaturges, prevents all spellcasting and speaking by the
                        target.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/earth">Petrification</NavLink> causes
                        part of the target's body to turn to stone, but these stone parts are resistance to physical
                        attacks. Full petrification turns the entire body to stone, immobilizing them entirely. Only
                        casters of earth magic can create this condition.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/time">Slowness</NavLink> reduces
                        the movement, attack, or casting speed of the target. <em>Temporal stop</em> is the greater form
                        of this status, only able to be caused by time thaumaturges. Stop completely freezes the target
                        in time, preventing all movement and damage.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/nature">Poison</NavLink> is
                        a status that causes magical damage over time through the use of a substance or spell.
                        The power can be strengthened into <em>dire poison</em> by nature wizards, which causes the
                        damage to increase while the poison stays untreated.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/spirit">Soulburn</NavLink> inflicts
                        a magical burn on a target. The afflicted anthro's spirit is set ablaze and causes decreased
                        magical attack and defense for a time. Treatment must be magical to relieve or remove the
                        internal burning sensation caused. <em>Dire soulburn</em> makes the target completely vulnerable
                        to all forms of magic.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/light">Blindness</NavLink> is an
                        effect that reduces the vision and accuracy of the afflicted. <em>Full blindness</em> completely
                        removes the vision of the target until they are capable of being healed or repenting. Only
                        light wizards can cause full blindness.
                    </p>
                    <p>
                        <NavLink className="world-link" to="/world/magic/elements/dark">Fear</NavLink> is a status
                        that reduces the willpower of the target. The enhanced version is deep terror, which can
                        only be used through dark magic. <em>Deep terror</em> completely breaks the will of the target
                        and can cause them to become incapacitated.
                    </p>
                </div>
            </ContentBox>
        </>
    );
}

export default MagicAfflictionsPage;